/**
 * Transaction Types Configuration
 * Hardcoded - not stored in Firestore
 *
 * This is the single source of truth for all transaction types in the system.
 */

export const TRAN_TYPES = {
  // Opening Balance
  OB: {
    code: 'OB',
    name: 'Opening Balance',
    prefix: 'OB-',
    affectsStock: true,
    affectsAccounting: true,
    statuses: ['Draft', 'Posted'],
    defaultStatus: 'Draft',
    partyType: null, // Can be customer, vendor, or null
    module: 'accounting',
    description: 'Opening balance entry for financial year'
  },

  // Purchase Module
  PO: {
    code: 'PO',
    name: 'Purchase Order',
    prefix: 'PO-',
    affectsStock: false,
    affectsAccounting: false,
    // 'Closed' = closed by hand with some quantity still pending (order kept as entered).
    statuses: ['Draft', 'Approved', 'Partially Received', 'Completed', 'Closed', 'Cancelled'],
    defaultStatus: 'Draft',
    partyType: 'vendor',
    module: 'purchases',
    canCreateChild: ['GRN', 'PI'],
    description: 'Request to vendor for goods/services'
  },

  GRN: {
    code: 'GRN',
    name: 'Goods Receipt Note',
    prefix: 'GRN-',
    affectsStock: false, // hardcoded default — Inventory Movement setting can override to 'grn' mode
    affectsAccounting: false,
    stockDirection: 'in', // used by stockFlowHelper when shouldPostStock() returns true
    statuses: ['Draft', 'Received', 'Invoiced', 'Cancelled'],
    defaultStatus: 'Draft',
    partyType: 'vendor',
    module: 'purchases',
    canCreateChild: ['PI'],
    description: 'Record receipt of goods from vendor'
  },

  PI: {
    code: 'PI',
    name: 'Purchase Invoice',
    prefix: 'PI-',
    affectsStock: true,
    affectsAccounting: true,
    statuses: ['Draft', 'Posted', 'Partial', 'Paid', 'Cancelled'],
    defaultStatus: 'Draft',
    partyType: 'vendor',
    module: 'purchases',
    stockDirection: 'in',
    description: 'Vendor bill for goods/services'
  },

  PR: {
    code: 'PR',
    name: 'Purchase Return',
    prefix: 'PR-',
    affectsStock: true,
    affectsAccounting: true,
    statuses: ['Draft', 'Posted', 'Cancelled'],
    defaultStatus: 'Draft',
    partyType: 'vendor',
    module: 'purchases',
    stockDirection: 'out',
    description: 'Return goods to vendor'
  },

  NPI: {
    code: 'NPI',
    name: 'Non-Tax Purchase Invoice',
    prefix: 'NPI-',
    affectsStock: true,
    affectsAccounting: true,
    statuses: ['Draft', 'Posted', 'Partial', 'Paid', 'Cancelled'],
    defaultStatus: 'Draft',
    partyType: 'vendor',
    module: 'purchases',
    stockDirection: 'in',
    taxExempt: true,
    description: 'Vendor bill without tax (WHT still applies)'
  },

  NPR: {
    code: 'NPR',
    name: 'Non-Tax Purchase Return',
    prefix: 'NPR-',
    affectsStock: true,
    affectsAccounting: true,
    statuses: ['Draft', 'Posted', 'Cancelled'],
    defaultStatus: 'Draft',
    partyType: 'vendor',
    module: 'purchases',
    stockDirection: 'out',
    taxExempt: true,
    description: 'Return goods to vendor (non-tax invoice)'
  },

  IPI: {
    code: 'IPI',
    name: 'Import Purchase Invoice',
    prefix: 'IPI-',
    affectsStock: true,
    affectsAccounting: true,
    statuses: ['Draft', 'Posted', 'Partial', 'Paid', 'Cancelled'],
    defaultStatus: 'Draft',
    partyType: 'vendor',
    module: 'purchases',
    stockDirection: 'in',
    foreignCurrency: true,
    description: 'Foreign vendor bill with duties, charges, and landed cost capitalized into inventory'
  },

  // Sales Module
  QT: {
    code: 'QT',
    name: 'Quotation',
    prefix: 'QT-',
    affectsStock: false,
    affectsAccounting: false,
    statuses: ['Draft', 'Sent', 'Accepted', 'Rejected', 'Expired', 'Cancelled'],
    defaultStatus: 'Draft',
    partyType: 'customer',
    module: 'sales',
    canCreateChild: ['SO'],
    description: 'Price quote to customer'
  },

  SO: {
    code: 'SO',
    name: 'Sale Order',
    prefix: 'SO-',
    affectsStock: false,
    affectsAccounting: false,
    // 'Closed' = closed by hand with some quantity still pending (order kept as entered).
    statuses: ['Draft', 'Approved', 'Partially Delivered', 'Completed', 'Closed', 'Cancelled'],
    defaultStatus: 'Draft',
    partyType: 'customer',
    module: 'sales',
    canCreateChild: ['DC', 'SI'],
    description: 'Customer order'
  },

  DC: {
    code: 'DC',
    name: 'Delivery Challan',
    prefix: 'DC-',
    affectsStock: false,
    // DC posts stock OUT only when the company's Inventory Movement setting is on
    // "Delivery Challan" mode — that gating lives upstream in shouldPostStock().
    // This direction tag is what the stock writer needs once it's told to post;
    // without it, generateStockFlow() bails and the DC never deducts stock even
    // in challan mode. (affectsStock stays false — it's only read by the fallback
    // path that DC never reaches, since DC is a configurable OUT doc type.)
    stockDirection: 'out',
    affectsAccounting: false,
    statuses: ['Draft', 'Dispatched', 'Delivered', 'Invoiced', 'Cancelled'],
    defaultStatus: 'Draft',
    partyType: 'customer',
    module: 'sales',
    canCreateChild: ['SI'],
    description: 'Delivery document'
  },

  SI: {
    code: 'SI',
    name: 'Sale Invoice',
    prefix: 'SI-',
    affectsStock: true,
    affectsAccounting: true,
    statuses: ['Draft', 'Posted', 'Partial', 'Paid', 'Cancelled'],
    defaultStatus: 'Draft',
    partyType: 'customer',
    module: 'sales',
    stockDirection: 'out',
    description: 'Bill to customer'
  },

  SR: {
    code: 'SR',
    name: 'Sale Return',
    prefix: 'SR-',
    affectsStock: true,
    affectsAccounting: true,
    statuses: ['Draft', 'Posted', 'Cancelled'],
    defaultStatus: 'Draft',
    partyType: 'customer',
    module: 'sales',
    stockDirection: 'in',
    description: 'Goods returned by customer'
  },

  NSI: {
    code: 'NSI',
    name: 'Non-Tax Sale Invoice',
    prefix: 'NSI-',
    affectsStock: true,
    affectsAccounting: true,
    statuses: ['Draft', 'Posted', 'Partial', 'Paid', 'Cancelled'],
    defaultStatus: 'Draft',
    partyType: 'customer',
    module: 'sales',
    stockDirection: 'out',
    taxExempt: true,
    description: 'Bill to customer without tax'
  },

  NSR: {
    code: 'NSR',
    name: 'Non-Tax Sale Return',
    prefix: 'NSR-',
    affectsStock: true,
    affectsAccounting: true,
    statuses: ['Draft', 'Posted', 'Cancelled'],
    defaultStatus: 'Draft',
    partyType: 'customer',
    module: 'sales',
    stockDirection: 'in',
    taxExempt: true,
    description: 'Goods returned by customer (non-tax invoice)'
  },

  // Receipts & Payments
  CR: {
    code: 'CR',
    name: 'Cash Receipt',
    prefix: 'CR-',
    affectsStock: false,
    affectsAccounting: true,
    statuses: ['Draft', 'Posted', 'Cancelled'],
    defaultStatus: 'Draft',
    partyType: 'customer',
    module: 'receipts',
    description: 'Receive cash payment from customer'
  },

  BR: {
    code: 'BR',
    name: 'Bank Receipt',
    prefix: 'BR-',
    affectsStock: false,
    affectsAccounting: true,
    statuses: ['Draft', 'Posted', 'Cancelled'],
    defaultStatus: 'Draft',
    partyType: 'customer',
    module: 'receipts',
    description: 'Receive bank payment from customer'
  },

  CP: {
    code: 'CP',
    name: 'Cash Payment',
    prefix: 'CP-',
    affectsStock: false,
    affectsAccounting: true,
    statuses: ['Draft', 'Posted', 'Cancelled'],
    defaultStatus: 'Draft',
    partyType: 'vendor',
    module: 'payments',
    description: 'Pay cash to vendor'
  },

  BP: {
    code: 'BP',
    name: 'Bank Payment',
    prefix: 'BP-',
    affectsStock: false,
    affectsAccounting: true,
    statuses: ['Draft', 'Posted', 'Cancelled'],
    defaultStatus: 'Draft',
    partyType: 'vendor',
    module: 'payments',
    description: 'Pay vendor via bank'
  },

  JV: {
    code: 'JV',
    name: 'Journal Voucher',
    prefix: 'JV-',
    affectsStock: false,
    affectsAccounting: true,
    statuses: ['Draft', 'Posted', 'Cancelled'],
    defaultStatus: 'Draft',
    partyType: null,
    module: 'accounting',
    manualEntry: true, // User enters debit/credit manually
    description: 'Manual accounting entry'
  },

  // Cash Book — daily cash register. One voucher per day per cash account.
  // Lines carry account + Dr/Cr; the selected cash account auto-posts the
  // balancing counter (built as manualLines by the form, like JV).
  CB: {
    code: 'CB',
    name: 'Cash Book',
    prefix: 'CB-',
    affectsStock: false,
    affectsAccounting: true,
    statuses: ['Draft', 'Posted', 'Cancelled'],
    defaultStatus: 'Draft',
    partyType: null,
    module: 'cashbook',
    manualEntry: true,
    description: 'Daily cash book — one voucher per day per cash account'
  },

  // Fixed Assets Module
  DEP: {
    code: 'DEP',
    name: 'Depreciation Run',
    prefix: 'DEP-',
    affectsStock: false,
    affectsAccounting: true,
    statuses: ['Draft', 'Posted', 'Cancelled'],
    defaultStatus: 'Posted',
    partyType: null,
    module: 'fixed_assets',
    manualEntry: true, // Dr Depreciation Expense / Cr Accumulated Depreciation per category
    description: 'Periodic depreciation charge across active fixed assets'
  },

  // Asset Appreciation (upward revaluation). Separate module, default off.
  APR: {
    code: 'APR',
    name: 'Asset Appreciation',
    prefix: 'APR-',
    affectsStock: false,
    affectsAccounting: true,
    statuses: ['Draft', 'Posted', 'Cancelled'],
    defaultStatus: 'Posted',
    partyType: null,
    module: 'fixed_assets',
    manualEntry: true, // Dr Fixed Asset account per asset / Cr Revaluation Surplus (equity)
    description: 'Upward revaluation of fixed assets'
  },

  FAD: {
    code: 'FAD',
    name: 'Fixed Asset Disposal',
    prefix: 'FAD-',
    affectsStock: false,
    affectsAccounting: true,
    statuses: ['Draft', 'Posted', 'Cancelled'],
    defaultStatus: 'Posted',
    partyType: null,
    module: 'fixed_assets',
    manualEntry: true, // Dr proceeds + accum dep / Cr asset cost +/- gain or loss
    description: 'Sale / scrap / write-off of a fixed asset'
  },

  // Store Module
  AS: {
    code: 'AS',
    name: 'Add Stock',
    prefix: 'AS-',
    affectsStock: true,
    affectsAccounting: true,
    statuses: ['Draft', 'Posted', 'Cancelled'],
    defaultStatus: 'Draft',
    partyType: null,
    module: 'store',
    stockDirection: 'in',
    description: 'Add inventory without purchase'
  },

  RS: {
    code: 'RS',
    name: 'Reduce Stock',
    prefix: 'RS-',
    affectsStock: true,
    affectsAccounting: true,
    statuses: ['Draft', 'Posted', 'Cancelled'],
    defaultStatus: 'Draft',
    partyType: null,
    module: 'store',
    stockDirection: 'out',
    description: 'Remove inventory without sale'
  },

  ST: {
    code: 'ST',
    name: 'Stock Transfer',
    prefix: 'ST-',
    affectsStock: true,
    affectsAccounting: false,
    statuses: ['Draft', 'Completed', 'Cancelled'],
    defaultStatus: 'Draft',
    partyType: null,
    module: 'store',
    stockDirection: 'both', // Creates both in and out entries
    manualStockFlow: true, // Stock flow handled manually (dual-location OUT+IN)
    description: 'Move stock between locations'
  },

  // Two-step transfer (dispatch → blind receive). Unlike ST (one doc, both legs
  // at Completed), these are TWO documents: Send deducts the source, Receive
  // adds the destination, linked by the Send's number. Stock-only, no accounting.
  SND: {
    code: 'SND',
    name: 'Send Inventory',
    prefix: 'SND-',
    affectsStock: true,
    affectsAccounting: false,
    statuses: ['Draft', 'Sent', 'Partially Received', 'Received', 'Cancelled'],
    defaultStatus: 'Draft',
    partyType: null,
    module: 'store',
    stockDirection: 'out',   // single OUT leg — deducts the source branch/location
    manualStockFlow: true,   // the form posts stock when saved as "Sent"
    description: 'Send stock to another branch/location (deducts source; receiver adds it back via Receive Inventory)'
  },
  RCV: {
    code: 'RCV',
    name: 'Receive Inventory',
    prefix: 'RCV-',
    affectsStock: true,
    affectsAccounting: false,
    statuses: ['Draft', 'Received', 'Cancelled'],
    defaultStatus: 'Draft',
    partyType: null,
    module: 'store',
    stockDirection: 'in',    // single IN leg — adds the destination branch/location
    manualStockFlow: true,   // the form posts stock on save
    description: 'Blind-receive stock against a Send Inventory number (adds destination)'
  },

  // Inward Gate Pass
  IGP: {
    code: 'IGP',
    name: 'Inward Gate Pass',
    prefix: 'IGP-',
    affectsStock: false,
    affectsAccounting: false,
    statuses: ['Pending', 'Verified', 'Closed', 'Cancelled'],
    defaultStatus: 'Pending',
    partyType: 'vendor',
    module: 'store',
    description: 'Record of goods entering the premises'
  },

  // Outward Gate Pass
  OGP: {
    code: 'OGP',
    name: 'Outward Gate Pass',
    prefix: 'OGP-',
    affectsStock: false,
    affectsAccounting: false,
    statuses: ['Pending', 'Dispatched', 'Closed', 'Cancelled'],
    defaultStatus: 'Pending',
    partyType: null,
    module: 'store',
    description: 'Record of goods leaving the premises'
  },

  // Bill of Materials
  BOM: {
    code: 'BOM',
    name: 'Bill of Materials',
    prefix: 'BOM-',
    affectsStock: false,
    affectsAccounting: false,
    statuses: ['Draft', 'Active', 'Obsolete', 'Cancelled'],
    defaultStatus: 'Draft',
    partyType: null,
    module: 'store',
    description: 'Material list for production'
  },

  // Production Order
  PRO: {
    code: 'PRO',
    name: 'Production Order',
    prefix: 'PRO-',
    affectsStock: true,
    affectsAccounting: true,
    statuses: ['Planned', 'Completed', 'Cancelled'],
    defaultStatus: 'Planned',
    partyType: null,
    module: 'store',
    stockDirection: 'both',
    manualStockFlow: true,
    manualAccounting: true,
    description: 'Order to produce finished goods from raw materials'
  },

  // Manual Production (no BOM) — module: store_manual_production
  // MIP issues raw materials to WIP; PRC receives finished goods back, clearing WIP.
  MIP: {
    code: 'MIP',
    name: 'Material Issue to Production',
    prefix: 'MIP-',
    affectsStock: true,
    affectsAccounting: true,
    // Kept to Draft/Posted/Cancelled so posted accounting/stock appear in reports
    // (reports include only status === 'Posted'). Receipt progress (open / partial /
    // fully received) is DERIVED from linked PRC receipts, not the transaction status.
    statuses: ['Draft', 'Posted', 'Cancelled'],
    defaultStatus: 'Draft',
    partyType: null,
    module: 'store',
    stockDirection: 'out',
    canCreateChild: ['PRC'],
    description: 'Issue raw materials to production (no BOM); cost parked in WIP'
  },

  PRC: {
    code: 'PRC',
    name: 'Production Receipt',
    prefix: 'PRC-',
    affectsStock: true,
    affectsAccounting: true,
    statuses: ['Draft', 'Posted', 'Cancelled'],
    defaultStatus: 'Draft',
    partyType: null,
    module: 'store',
    stockDirection: 'in',
    description: 'Receive finished goods back from production; clears WIP into finished-goods inventory'
  },

  // Purchase Requisition
  REQ: {
    code: 'REQ',
    name: 'Purchase Requisition',
    prefix: 'REQ-',
    affectsStock: false,
    affectsAccounting: false,
    statuses: ['Draft', 'Submitted', 'Approved', 'Rejected', 'Cancelled'],
    defaultStatus: 'Draft',
    partyType: null,
    module: 'store',
    canCreateChild: ['PO'],
    description: 'Internal request for purchasing items'
  },

  // Salary Module
  ADV: {
    code: 'ADV',
    name: 'Salary Advance',
    prefix: 'ADV-',
    affectsStock: false,
    affectsAccounting: true,
    statuses: ['Draft', 'Issued', 'Recovered', 'Cancelled'],
    defaultStatus: 'Draft',
    partyType: null,
    module: 'salary',
    description: 'Short-term cash advance against future salary; auto-recovered in next Payroll Run'
  },

  LON: {
    code: 'LON',
    name: 'Staff Loan',
    prefix: 'LON-',
    affectsStock: false,
    affectsAccounting: true,
    statuses: ['Draft', 'Active', 'Closed', 'Cancelled'],
    defaultStatus: 'Draft',
    partyType: null,
    module: 'salary',
    description: 'Long-term loan to staff with monthly installment recovery from Payroll Run'
  },

  // Loan / advance paid back by the staff in cash or bank (outside payroll).
  // Dr Cash/Bank, Cr Staff Account; lowers the loans' / advances' balances
  // (see utils/loanRecoveryHelper.js).
  LRC: {
    code: 'LRC',
    name: 'Loan Recovery',
    prefix: 'LRC-',
    affectsStock: false,
    affectsAccounting: true,
    statuses: ['Draft', 'Posted', 'Cancelled'],
    defaultStatus: 'Draft',
    partyType: null,
    module: 'salary',
    description: 'Staff loan / salary advance received back in cash or bank, outside payroll'
  },

  PAY: {
    code: 'PAY',
    name: 'Payroll Run',
    prefix: 'PAY-',
    affectsStock: false,
    affectsAccounting: true,
    statuses: ['Draft', 'Posted', 'Paid', 'Cancelled'],
    defaultStatus: 'Draft',
    partyType: null,
    module: 'salary',
    manualAccounting: true,
    description: 'Monthly payroll batch with attendance-based proration; recovers loans/advances; bulk-pays via Bank Payment'
  },

  FNF: {
    code: 'FNF',
    name: 'Final Settlement',
    prefix: 'FNF-',
    affectsStock: false,
    affectsAccounting: true,
    statuses: ['Draft', 'Posted', 'Paid', 'Cancelled'],
    defaultStatus: 'Draft',
    partyType: null,
    module: 'salary',
    manualAccounting: true,
    description: 'Full and final settlement when staff leaves: outstanding loan/advance + last salary + gratuity + leave encashment'
  },

  // POS Module
  PS: {
    code: 'PS',
    name: 'POS Sale',
    prefix: 'PS-',
    affectsStock: true,
    affectsAccounting: true,
    statuses: ['Posted', 'Cancelled'],
    defaultStatus: 'Posted',
    partyType: 'customer',
    module: 'pos',
    stockDirection: 'out',
    description: 'Point of Sale counter transaction'
  }
};

/**
 * System Account Codes for auto-posting
 * These MUST exist in Chart of Accounts
 */
export const SYSTEM_ACCOUNTS = {
  // Assets
  CASH_IN_HAND: '11101001',
  BANK_PRIMARY: '11102001',
  TRADE_RECEIVABLES_PARENT: '11201',
  STAFF_ACCOUNTS_PARENT: '11204',  // Parent for individual Salary Staff Level-5 accounts (sibling of Trade Debtors / Other Receivables under 112 Receivables)
  WHT_RECEIVABLE: '11205001',      // WHT / advance income tax withheld by customers on sales (asset, recoverable)
  INVENTORY_CONTROL: '11301001',
  WIP_INVENTORY: '11302001', // Work-in-Progress Inventory

  // Liabilities
  TRADE_PAYABLES_PARENT: '21101',
  GST_OUTPUT: '21201001',
  GST_INPUT: '21202001',
  WHT_PAYABLE: '21203001', // Withholding Tax Payable (Services) — default
  WHT_PAYABLE_SUPPLIES: '21203002', // Withholding Tax Payable (Supplies) — selectable on Sale Invoice

  // Equity
  RETAINED_EARNINGS: '32101001', // Accumulated retained earnings
  OPENING_BALANCE_EQUITY: '32102001', // Under Accumulated Profits > Opening Balance Equity

  // Revenue
  SALES_GENERAL: '41101001',
  SALES_RETURNS: '41102001',
  CARRIAGE_RECOVERED: '42102001',       // Transportation/carriage recovered on Sale Invoices (non-taxable)
  OTHER_CHARGES_RECOVERED: '42102002',  // Other charges recovered on Sale Invoices (non-taxable)

  // Expenses
  COGS: '51101001',
  STOCK_ADJUSTMENT: '51102001',
  PURCHASE_DISCOUNT: '51103001', // Purchase Discount (contra expense)
  CARRIAGE_INWARDS: '51104001',        // Transportation/carriage charged on Purchase Invoices (non-taxable)
  OTHER_CHARGES_PURCHASE: '51105001',  // Other charges on Purchase Invoices (non-taxable)
  PRODUCTION_WASTAGE: '51106001',      // Raw material wasted/scrapped in manual production (write-off)
  SALARY_EXPENSE: '52101001',    // Salaries Expense (postable L5 under 52101 Salaries & Wages)
  BONUS_EXPENSE: '52101002',     // Bonus & Incentives Expense (postable L5 under 52101 Salaries & Wages)

  // Fixed Assets module (postable L5 system accounts; per-category asset/accum-dep
  // accounts are resolved via the fixedAssetCategories seed, not here)
  DEPRECIATION_EXPENSE: '52108001', // Depreciation Expense (L5 under 52108)
  GAIN_ON_DISPOSAL: '42103001',     // Gain on Asset Disposal (L5 under 42103)
  LOSS_ON_DISPOSAL: '52115001'      // Loss on Asset Disposal (L5 under 52115)
  // Asset Appreciation's Revaluation Surplus account has no fixed code: it is
  // made on first use and found by its marker (fixedAssetHelper.findRevaluationSurplusAccount).
};

/**
 * Get TranType configuration by code
 */
export const getTranType = (code) => {
  return TRAN_TYPES[code] || null;
};

/**
 * Check if a TranType affects stock
 */
export const affectsStock = (code, hasParentLink = false) => {
  const tranType = TRAN_TYPES[code];
  if (!tranType) return false;

  // For conditional stock (PI, SI), check if there's a parent link
  if (tranType.conditionalStock && hasParentLink) {
    return false;
  }

  return tranType.affectsStock;
};

/**
 * Check if a TranType affects accounting
 */
export const affectsAccounting = (code) => {
  const tranType = TRAN_TYPES[code];
  return tranType ? tranType.affectsAccounting : false;
};

/**
 * Get stock direction for a TranType
 */
export const getStockDirection = (code) => {
  const tranType = TRAN_TYPES[code];
  return tranType ? tranType.stockDirection : null;
};

export default TRAN_TYPES;
